import React, { useState, useMemo } from 'react';
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, ChefHat } from 'lucide-react';
import { Recipe } from '@/types/recipe';

interface RecipeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedRecipeIds: string[]) => void;
  recipes: Recipe[];
  selectedRecipeIds: string[];
}

const RecipeSelectionModal: React.FC<RecipeSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  recipes,
  selectedRecipeIds
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedRecipeIds);

  // Sync temp selection when prop changes
  React.useEffect(() => {
    if (isOpen) {
      setTempSelectedIds(selectedRecipeIds);
    }
  }, [selectedRecipeIds, isOpen]);

  const filteredRecipes = useMemo(() => {
    if (!searchTerm.trim()) return recipes;
    return recipes.filter(recipe =>
      recipe.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [recipes, searchTerm]);

  const handleToggleRecipe = (recipeId: string) => {
    setTempSelectedIds(prev => 
      prev.includes(recipeId)
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const handleDone = () => {
    onSelect(tempSelectedIds);
    onClose();
  };

  const handleClose = () => {
    setTempSelectedIds(selectedRecipeIds);
    setSearchTerm('');
    onClose();
  };

  return (
    <AdaptiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select Recipes"
      size="lg"
      scrollable={true}
    >
      <div className="flex flex-col h-full">
        {/* Search Header */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {tempSelectedIds.length} recipe{tempSelectedIds.length !== 1 ? 's' : ''} selected
          </div>
        </div>

        {/* Recipe List */}
        <div className="flex-1 overflow-auto">
          {filteredRecipes.length > 0 ? (
            <div className="p-2">
              {filteredRecipes.map((recipe) => {
                const isSelected = tempSelectedIds.includes(recipe.id);
                return (
                  <div
                    key={recipe.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => handleToggleRecipe(recipe.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleRecipe(recipe.id)}
                      className="flex-shrink-0"
                    />
                    <ChefHat size={16} className="text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{recipe.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Prep: {recipe.prepTime || 'N/A'} • Cook: {recipe.cookTime || 'N/A'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <ChefHat size={48} className="mx-auto mb-2 opacity-50" />
              <p>No recipes found</p>
              {searchTerm && (
                <p className="text-sm mt-1">Try different search terms</p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t bg-background">
          <div className="flex gap-3">
            <Button
              onClick={handleDone}
              className="flex-1"
              disabled={tempSelectedIds.length === 0}
            >
              Done ({tempSelectedIds.length})
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </AdaptiveModal>
  );
};

export default RecipeSelectionModal;
